(() => {
  const gap = 12;
  const managedProperties = ['bottom', 'max-height', 'visibility', 'pointer-events'];
  const obstacleSelector = 'sticky-atc-panel.fixed.bottom-0, .compare-bar';
  const states = ['homie-launcher-button', 'homie-chatbot-widget'].map((id) => ({
    id, element: null, originals: {}, applied: {}, signature: ''
  }));
  const observedObstacles = new Map();
  let homieRoot = null;
  let scheduled = false;
  let trackingUntil = 0;
  let discoveryTimer = null;
  let discoveryAttempts = 0;

  function rememberStyle(element, property) {
    return {
      value: element.style.getPropertyValue(property),
      priority: element.style.getPropertyPriority(property)
    };
  }

  function restoreStyle(state, property) {
    const original = state.originals[property];
    if (original.value) {
      state.element.style.setProperty(property, original.value, original.priority);
    } else {
      state.element.style.removeProperty(property);
    }
    delete state.applied[property];
  }

  function externalSignature(element) {
    return ['display', 'width', 'height', 'right', 'left', 'top', 'transform']
      .map((property) => element.style.getPropertyValue(property))
      .concat(element.className, element.hidden, element.getAttribute('aria-expanded')).join('|');
  }

  function readExternalStyles(state) {
    for (const property of managedProperties) {
      const current = rememberStyle(state.element, property);
      const applied = state.applied[property];
      if (!applied || current.value !== applied.value || current.priority !== applied.priority) {
        state.originals[property] = current;
      }
    }
    state.signature = externalSignature(state.element);
  }

  function isVisible(element) {
    if (!element || element.hidden || !element.getClientRects().length) return false;
    const style = getComputedStyle(element);
    return style.display !== 'none' && style.visibility === 'visible' && Number(style.opacity) > 0;
  }

  function applyStyle(state, property, value) {
    state.element.style.setProperty(property, value, 'important');
    state.applied[property] = rememberStyle(state.element, property);
  }

  function getViewport() {
    const viewport = window.visualViewport;
    return {
      top: viewport ? viewport.offsetTop : 0,
      bottom: viewport ? Math.min(innerHeight, viewport.offsetTop + viewport.height) : innerHeight,
      left: viewport ? viewport.offsetLeft : 0,
      right: viewport ? viewport.offsetLeft + viewport.width : innerWidth
    };
  }

  function getObstacleRects(viewport) {
    const rects = [];
    for (const element of document.querySelectorAll(obstacleSelector)) {
      if (!isVisible(element)) continue;
      if (element.matches('sticky-atc-panel') && (
        element.classList.contains('sticky-atc-panel--out')
        || document.body.classList.contains('scrolled-to-bottom')
      )) continue;
      const elements = [element];
      const toggle = element.querySelector('.js-compare-bar-toggle');
      if (toggle) elements.push(toggle);
      for (const item of elements) {
        if (!isVisible(item)) continue;
        const rect = item.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0 && rect.bottom > viewport.top && rect.top < viewport.bottom) {
          rects.push(rect);
        }
      }
    }
    return rects.sort((a, b) => b.top - a.top);
  }

  function positionElement(state, rects, viewport) {
    const element = state.element;
    if (!element?.isConnected) return;
    readExternalStyles(state);
    for (const property of Object.keys(state.applied)) restoreStyle(state, property);
    if (!isVisible(element) || getComputedStyle(element).position !== 'fixed') return;

    const rect = element.getBoundingClientRect();
    const style = getComputedStyle(element);
    const baseBottom = parseFloat(style.bottom);
    if (!Number.isFinite(baseBottom)) return;
    let bottom = Math.max(baseBottom, innerHeight - viewport.bottom + gap);

    for (const obstacle of rects) {
      if (rect.right <= obstacle.left || rect.left >= obstacle.right) continue;
      const targetBottom = innerHeight - bottom;
      if (targetBottom > obstacle.top - gap && targetBottom - rect.height < obstacle.bottom + gap) {
        bottom = Math.max(bottom, innerHeight - obstacle.top + gap);
      }
    }

    const availableHeight = Math.max(0, innerHeight - bottom - viewport.top - gap);
    if (state.id === 'homie-launcher-button' && rect.height > availableHeight) {
      applyStyle(state, 'visibility', 'hidden');
      applyStyle(state, 'pointer-events', 'none');
      return;
    }
    if (bottom > baseBottom) applyStyle(state, 'bottom', `${Math.ceil(bottom)}px`);
    if (state.id === 'homie-chatbot-widget') {
      if (rect.height > availableHeight) {
        applyStyle(state, 'max-height', `${Math.floor(availableHeight)}px`);
      }
    }
  }

  const homieObserver = new MutationObserver((records) => {
    const changed = records.some((record) => {
      if (record.type === 'childList') return true;
      const state = states.find((item) => item.element === record.target);
      return state && (state.signature !== externalSignature(state.element)
        || managedProperties.some((property) => {
          const expected = state.applied[property] || state.originals[property];
          const current = rememberStyle(state.element, property);
          return expected && (current.value !== expected.value || current.priority !== expected.priority);
        }));
    });
    if (changed) requestUpdate();
  });

  const resizeObserver = new ResizeObserver(() => requestUpdate());

  function connectElements() {
    const host = document.getElementById('homie-shadow-host');
    const root = host?.shadowRoot || null;
    if (root !== homieRoot) {
      homieObserver.disconnect();
      homieRoot = root;
      if (root) homieObserver.observe(root, {
        childList: true, subtree: true, attributes: true,
        attributeFilter: ['style', 'class', 'hidden', 'aria-expanded']
      });
    }
    for (const state of states) {
      const element = document.getElementById(state.id) || root?.getElementById(state.id) || null;
      if (element === state.element) continue;
      if (state.element) resizeObserver.unobserve(state.element);
      state.element = element;
      state.originals = {};
      state.applied = {};
      if (element) {
        readExternalStyles(state);
        resizeObserver.observe(element);
      }
    }
    const obstacles = new Set(document.querySelectorAll(obstacleSelector));
    for (const [element, observer] of observedObstacles) {
      if (obstacles.has(element)) continue;
      observer.disconnect();
      resizeObserver.unobserve(element);
      observedObstacles.delete(element);
    }
    for (const element of obstacles) {
      if (observedObstacles.has(element)) continue;
      const observer = new MutationObserver(() => {
        trackingUntil = Math.max(trackingUntil, performance.now() + 450);
        requestUpdate();
      });
      observer.observe(element, {
        attributes: true, subtree: true,
        attributeFilter: ['class', 'style', 'hidden', 'aria-expanded']
      });
      resizeObserver.observe(element);
      observedObstacles.set(element, observer);
    }
    if (host && !root && !discoveryTimer && discoveryAttempts < 20) {
      discoveryAttempts += 1;
      discoveryTimer = setTimeout(() => {
        discoveryTimer = null;
        requestUpdate();
      }, 250);
    }
  }

  function update() {
    scheduled = false;
    connectElements();
    if (states.some((state) => state.element)) {
      const viewport = getViewport();
      const rects = getObstacleRects(viewport);
      for (const state of states) positionElement(state, rects, viewport);
    }
    if (performance.now() < trackingUntil) requestUpdate();
  }

  function requestUpdate() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(update);
  }

  const structureObserver = new MutationObserver((records) => {
    const relevant = records.some((record) => record.type === 'attributes'
      ? record.target === document.body
      : [...record.addedNodes, ...record.removedNodes].some((node) => node.nodeType === 1 && (
        node.matches(`${obstacleSelector}, #homie-shadow-host`)
        || node.querySelector(`${obstacleSelector}, #homie-shadow-host`)
      )));
    if (relevant) requestUpdate();
  });
  structureObserver.observe(document.body, {
    childList: true, subtree: true, attributes: true, attributeFilter: ['class']
  });

  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate, { passive: true });
  window.addEventListener('orientationchange', requestUpdate, { passive: true });
  window.addEventListener('pageshow', requestUpdate);
  window.addEventListener('load', requestUpdate);
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', requestUpdate, { passive: true });
    window.visualViewport.addEventListener('scroll', requestUpdate, { passive: true });
  }
  requestUpdate();
})();
